/**
 * 🦊 **The melrakki — the arctic fox.** T288, decision 134.
 *
 * Iceland's only native land mammal, here since the last Ice Age, and the
 * game's first WILD animal and first PREDATOR. 📖 Every number below is on
 * decision 134's row in `Planning/00_Project_Brief.md`, with its source.
 *
 * 🔴 **Read `Development/14_The_Animal_System.md` before changing anything
 * here** — decision 117: an animal is a thing the server keeps, never a
 * formula. This file is the fox's BRAIN and its TERRITORIES; the server keeps
 * the foxes (`server/src/foxes.ts`) and the client draws them (`foxmesh.ts`).
 *
 * | What it does | Row | 🎯 haugur's answer |
 * |---|---|---|
 * | 😴 sleeps curled, game hours 09–17 | `Sleep` | *"Knife, sneak up"* |
 * | 🤫 wakes if you RUN within 12 m or WALK within 4 m; sneaking never wakes it | `Alert` | the sneak key, Z held |
 * | 👁️ awake, sees you at 30 m: sits and stares | `Watch` | *"Watches, then runs"* |
 * | 🏃 trots off at 15 m, sprints if you rush it | `Flee` | |
 * | 🐑 takes a lamb outside a pen, runs off with it | `Stalk`, `Carry` | *"Carries it off in its jaws"* |
 * | 🐭 sniffs the ground, pounces nose-first | `Sniff`, `Pounce` | pounce, sniff, sit, tail |
 */

import type { Behaviour, Ground, Species, Vec3 } from './creature.ts';
import { placeOnGround, rngFrom, seedFrom, wander, withBody } from './creature.ts';
import { isFooting } from './sheepmind.ts';

/** 🦊 The kind on the wire and in the database. */
export const FOX_KIND = 'refur';

/**
 * 📏 Nose to tail tip, metres — the body is drawn at this length. 🎯 haugur,
 * 2026-10-04, having seen it on staging: *"increase the size of the foxes by
 * 25%"* — it was 0.8, and the height and width went up with it. ⚠️ A little
 * over a real arctic fox (~85 cm with the tail), within reach of a big male.
 */
export const FOX_LENGTH = 1.0;
/** 📏 Standing height to the ears, metres. 25–30 cm at the shoulder. */
export const FOX_HEIGHT = 0.5;
/** 📏 Across the body, metres — the square footprint (`withBody`). */
export const FOX_WIDTH = 0.375;
/** 🚶 Its calm walk, m/s. Every other speed is a `pace` multiple of this. */
export const FOX_SPEED = 1.2;
/**
 * 🦊 A trot, as a pace: 1.8 m/s. 🎯 haugur, 2026-10-04, on staging: *"it is too
 * fast i have a hard time looking at it … slow it down"* — it was 2.4.
 */
export const FOX_TROT = 1.5;
/**
 * 🏃 A sprint, as a pace: 7.2 m/s. ⚠️ It was 10.8, from a proposal that said
 * you run at "about 8" — you run at 12 (`movement.ts`), and nobody checked.
 * 🎯 Slowed at haugur's word so a fox can be LOOKED at. ⚠️ A running player is
 * now faster than a sprinting fox: an awake one can be run down in the open.
 */
export const FOX_SPRINT = 6;

/** 😴 It sleeps from this game hour… */
export const FOX_SLEEP_FROM = 9;
/** 😴 …to this one. Awake at night, dawn and dusk. */
export const FOX_SLEEP_TO = 17;

/** 🤫 A RUNNING player wakes a sleeper inside this, metres. */
export const WAKE_RUNNING = 12;
/** 🤫 A WALKING player wakes a sleeper inside this, metres. */
export const WAKE_WALKING = 4;
/**
 * 🤫 **Faster than this is running, m/s.** You walk at 5.6 and run at 12.
 * ⚠️ The server measures speed from position updates, so the cut sits between.
 */
export const RUNNING_SPEED = 8;
/**
 * 🤫 **Faster than this is walking, m/s.** Sneaking (Z) is half a walk, 2.8,
 * and never wakes a fox — that is the whole of decision 134's hunt.
 */
export const WALKING_SPEED = 4.2;
/** ⏰ Once woken it stays awake this long after you were last near, seconds. */
export const STAY_AWAKE = 90;

/** 👁️ An awake fox notices you this far off, metres, and sits to stare. */
export const FOX_NOTICE = 30;
/** 🏃 And trots off when you come this close, metres. */
export const FOX_FLEE = 15;
/**
 * 🏃 It sprints when you are this close, or RUNNING ({@link RUNNING_SPEED}).
 * 🔴 It used to sprint at anything faster than {@link WALKING_SPEED} — so your
 * ordinary 5.6 m/s walk made every fox bolt, and haugur could not look at one.
 */
export const FOX_BOLT = 5;

/** 🐑 How far a fox will go for a lamb, metres. */
export const FOX_PREY_REACH = 40;
/** 🐑 How close it must be to take one, metres. */
export const FOX_GRAB = 1;
/** 🐑 Kill it before it is this far from every player, or the lamb is gone, metres. */
export const FOX_GETAWAY = 150;

/**
 * 🗺️ **The territory grid, metres.** One den per square kilometre of land —
 * about ten times the real density (~9,000 foxes on 103,000 km²), on purpose,
 * or nobody would ever meet one.
 */
export const FOX_DEN_CELL = 1000;
/** 🏠 How far from its den a calm fox wanders, metres. */
export const FOX_RANGE = 18;
/** ⏳ A den whose fox was killed stands empty this many game days (1 real hour). */
export const FOX_EMPTY_DAYS = 3;

/** 🎁 What a killed fox gives — 🎯 *"gives 2 fox pellets, meat is coockable"*. */
export const FOX_SKIN_ITEM = 'refaskinn';
export const FOX_SKINS = 2;
export const FOX_MEAT_ITEM = 'refakjot';
export const FOX_MEAT = 1;

/** 😴 Is this game hour a fox's sleeping time? */
export function isSleepHour(hour: number | undefined): boolean {
  return hour !== undefined && hour >= FOX_SLEEP_FROM && hour < FOX_SLEEP_TO;
}

/**
 * 🤫 **Does a player at this distance and speed wake a sleeping fox?**
 *
 * 🔴 **This is the hunt, in one line.** Run near it and it is gone; walk up
 * and it hears you at four metres, outside the knife's three; sneak and you
 * can reach it.
 */
export function wakes(distance: number, speed: number): boolean {
  if (speed > RUNNING_SPEED) return distance < WAKE_RUNNING;
  if (speed > WALKING_SPEED) return distance < WAKE_WALKING;
  return false;
}

/** 😴 Is this fox asleep right now? The `Alert` row keeps the answer in memory. */
export function isAsleep(memory: Readonly<Record<string, number>>): boolean {
  return memory.asleep === 1;
}

/** 🐑 Is this fox carrying a lamb? */
export function isCarrying(memory: Readonly<Record<string, number>>): boolean {
  return memory.carrying === 1;
}

/**
 * 🎨 **Which coat a fox wears** — two in three dark ("blue"), one in three
 * white, fixed for its life by its id. 📖 65–70% of Icelandic foxes are the
 * blue morph (Perlan).
 */
export function foxCoat(id: string): 'dark' | 'white' {
  return rngFrom(seedFrom(`coat:${id}`))() < 2 / 3 ? 'dark' : 'white';
}

/**
 * ❄️ **Is a white fox in its winter coat this month?** November to April.
 * The dark ones are dark all year.
 */
export function isWinterCoat(month: number): boolean {
  return month >= 11 || month <= 4;
}

/** 🗺️ A den: where it is, and the key its fox and its emptiness go by. */
export interface Den {
  readonly key: string;
  readonly x: number;
  readonly z: number;
}

/**
 * 🗺️ **The den in one grid cell.** Placed by a hash of the cell, so every
 * server and every restart agrees — **nothing is stored to know where a den
 * is.** ⚠️ Whether a fox can actually live there (land, not glacier, not sea)
 * is the server's question, asked of the real ground.
 */
export function denIn(cx: number, cz: number): Den {
  const rng = rngFrom(seedFrom(`den:${cx},${cz}`));
  const margin = 100;
  const span = FOX_DEN_CELL - 2 * margin;
  return {
    key: `${cx},${cz}`,
    x: cx * FOX_DEN_CELL + margin + rng() * span,
    z: cz * FOX_DEN_CELL + margin + rng() * span,
  };
}

/** 🗺️ **Every den within `reach` metres of a point.** */
export function densNear(x: number, z: number, reach: number): Den[] {
  const out: Den[] = [];
  const x0 = Math.floor((x - reach) / FOX_DEN_CELL);
  const x1 = Math.floor((x + reach) / FOX_DEN_CELL);
  const z0 = Math.floor((z - reach) / FOX_DEN_CELL);
  const z1 = Math.floor((z + reach) / FOX_DEN_CELL);
  for (let cx = x0; cx <= x1; cx++) {
    for (let cz = z0; cz <= z1; cz++) {
      const den = denIn(cx, cz);
      if (Math.hypot(den.x - x, den.z - z) <= reach) out.push(den);
    }
  }
  return out;
}

/** 🦊 The id a den's fox goes by — one fox per den, so it is the den's key. */
export function foxIdOf(den: Den): string {
  return `${FOX_KIND}-${den.key}`;
}

/* ── The brain ─────────────────────────────────────────────────────────── */

/** A point `distance` metres from `self`, directly away from `from`. */
function awayFrom(self: Vec3, from: Vec3, distance: number, fallback: number): Vec3 {
  let angle = Math.atan2(self.z - from.z, self.x - from.x);
  if (!Number.isFinite(angle) || (self.x === from.x && self.z === from.z)) angle = fallback;
  return {
    x: self.x + Math.cos(angle) * distance,
    y: self.y,
    z: self.z + Math.sin(angle) * distance,
  };
}

/**
 * 🤫 **Alert — it holds no flag, so it always runs, and it only REMEMBERS.**
 *
 * `choose()` asks every flagless row whether it wants to run, every tick, and
 * that is how a thing that only notices joins the list without fighting
 * anybody for the body (`creature.ts`, `choose`). It keeps two numbers:
 * `asleep` and `alarmedAt`, and every row below reads them.
 */
function alert(priority: number): Behaviour {
  return {
    name: 'Alert',
    priority,
    flags: [],
    wants(sense) {
      const m = sense.self.memory;
      const p = sense.nearestPlayer;
      /* 🔴 A fox on its FIRST beat has no memory of sleeping, and read as awake —
         so it noticed a player 20 m off and bolted (a test found it). Asleep is
         the sleeping hours until it has been woken. */
      const sleeping = m.asleep === undefined ? isSleepHour(sense.hour) : isAsleep(m);
      if (p !== null) {
        const noticed = sleeping ? wakes(p.distance, p.speed) : p.distance < FOX_NOTICE;
        if (noticed) m.alarmedAt = sense.now;
      }
      const alarmed = m.alarmedAt !== undefined && sense.now - m.alarmedAt < STAY_AWAKE;
      m.asleep = isSleepHour(sense.hour) && !alarmed && !isCarrying(m) ? 1 : 0;
      return false;
    },
    act: () => ({}),
  };
}

/** 🐑 **Carry — a lamb in its jaws, away from everybody.** */
function carry(priority: number): Behaviour {
  return {
    name: 'Carry',
    priority,
    flags: ['move', 'look'],
    wants: (sense) => isCarrying(sense.self.memory),
    act(sense) {
      const self = sense.self;
      const p = sense.nearestPlayer;
      const from = p?.at ?? {
        x: self.memory.grabX ?? self.at.x,
        y: self.at.y,
        z: self.memory.grabZ ?? self.at.z,
      };
      const urgent = p !== null && p.distance < FOX_NOTICE;
      if (self.goal === null || urgent) {
        return {
          goal: awayFrom(self.at, from, 15, self.heading),
          pace: urgent ? FOX_SPRINT : FOX_TROT,
          turn: 3,
          lookAt: null,
        };
      }
      return { goal: self.goal, pace: FOX_TROT, lookAt: null };
    },
  };
}

/** 😴 **Sleep — curled up, nose under its tail.** */
function sleep(priority: number): Behaviour {
  return {
    name: 'Sleep',
    priority,
    flags: ['move', 'look'],
    wants: (sense) => isAsleep(sense.self.memory),
    act: () => ({ hold: true, lookAt: null }),
  };
}

/**
 * 🏃 **Flee — trot off at 15 m, sprint if rushed, and keep going to 30.**
 * `fleeing` is remembered so a fox at 20 m does not stop to sit again halfway.
 */
function flee(priority: number): Behaviour {
  return {
    name: 'Flee',
    priority,
    flags: ['move', 'look'],
    wants(sense) {
      const p = sense.nearestPlayer;
      const m = sense.self.memory;
      if (p === null || p.distance > FOX_NOTICE) {
        m.fleeing = 0;
        return false;
      }
      if (p.distance < FOX_FLEE) m.fleeing = 1;
      return m.fleeing === 1;
    },
    act(sense) {
      const self = sense.self;
      const p = sense.nearestPlayer;
      if (p === null) return {};
      const bolt = p.distance < FOX_BOLT || p.speed > RUNNING_SPEED;
      return {
        goal: awayFrom(self.at, p.at, 12, self.heading),
        pace: bolt ? FOX_SPRINT : FOX_TROT,
        turn: 3,
        lookAt: null,
      };
    },
  };
}

/** 👁️ **Watch — sits on its haunches and stares.** The client draws the sit. */
function watch(priority: number): Behaviour {
  return {
    name: 'Watch',
    priority,
    flags: ['move', 'look'],
    wants: (sense) => sense.nearestPlayer !== null && sense.nearestPlayer.distance < FOX_NOTICE,
    act: (sense) => ({ hold: true, lookAt: sense.nearestPlayer?.at ?? null }),
  };
}

/** 🐑 **Stalk — walk to the lamb the server pointed at.** Taking it is the server's. */
function stalk(priority: number): Behaviour {
  return {
    name: 'Stalk',
    priority,
    flags: ['move', 'look'],
    wants: (sense) => (sense.prey ?? null) !== null,
    act: (sense) => ({ goal: sense.prey ?? null, pace: FOX_TROT, lookAt: sense.prey ?? null }),
  };
}

/** 🐭 **Pounce — the mousing leap**, about every half minute when nothing is on. */
function pounce(priority: number): Behaviour {
  return {
    name: 'Pounce',
    priority,
    flags: ['move'],
    wants(sense) {
      const m = sense.self.memory;
      if (sense.now < (m.pounceUntil ?? 0)) return true;
      if (sense.self.rng() > 1 / 300) return false;
      m.pounceUntil = sense.now + 1.3;
      return true;
    },
    act: () => ({ hold: true }),
  };
}

/** 👃 **Sniff — a slow walk, nose to the ground**, now and then for a few seconds. */
function sniff(priority: number): Behaviour {
  return {
    name: 'Sniff',
    priority,
    flags: ['move'],
    wants(sense) {
      const m = sense.self.memory;
      if (sense.now < (m.sniffUntil ?? 0)) return true;
      if (sense.self.rng() > 1 / 120) return false;
      m.sniffUntil = sense.now + 5 + sense.self.rng() * 5;
      return true;
    },
    act: (sense) => ({ goal: sense.self.goal, pace: 0.6 }),
  };
}

/** 👀 Its head follows you while it is awake and you are in sight. */
function eyeOnYou(priority: number): Behaviour {
  return {
    name: 'Eye',
    priority,
    flags: ['look'],
    wants: (sense) =>
      !isAsleep(sense.self.memory) &&
      sense.nearestPlayer !== null &&
      sense.nearestPlayer.distance < FOX_NOTICE,
    act: (sense) => ({ lookAt: sense.nearestPlayer?.at ?? null }),
  };
}

/** 🦶 Clear air for a fox's body — not a wall, not the sea, head room above. */
function foxClear(ground: Ground, at: Vec3): boolean {
  if (ground.water(at.x, at.y, at.z)) return false;
  if (ground.solid(at.x, at.y, at.z)) return false;
  return !ground.solid(at.x, at.y + FOX_HEIGHT * 0.8, at.z);
}

/** 🦶 Something a fox will stand on — the sheep's footing rules: no roofs, beds or trees. */
function foxSupported(ground: Ground, at: Vec3): boolean {
  if (!ground.solid(at.x, at.y - 0.5, at.z)) return false;
  return isFooting(ground.block(at.x, at.y - 0.5, at.z));
}

/**
 * 🦊 **The fox.** THE ORDER OF THIS LIST IS THE DESIGN — lower runs first.
 *
 * Carrying a lamb beats everything; sleep beats noticing (a sleeper does not
 * flee — `Alert` wakes it first); fleeing beats watching; nothing calm runs
 * while you are in sight.
 */
export function fox(): Species {
  return {
    kind: FOX_KIND,
    behaviours: [
      alert(-3),
      carry(-2),
      sleep(-1),
      flee(0),
      watch(1),
      stalk(2),
      pounce(3),
      sniff(4),
      wander(9, 8),
      eyeOnYou(10),
    ],
    physique: { speed: FOX_SPEED, turn: 5, step: 1, drop: 3 },
    wander: { radius: FOX_RANGE, candidates: 10 },
    place: placeOnGround(6, 1),
    /* A fox has no favourite ground yet — what it hunts is in its rows. */
    score: () => 0,
    school: null,
    fits: withBody(foxClear, foxSupported, FOX_WIDTH),
    body: { width: FOX_WIDTH, height: FOX_HEIGHT },
  };
}
